import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { VirtualTour } from '@serasa/shared/components/virtual-tour/interfaces/virtual-tour-model.interface';
import { VirtualTourService } from '@serasa/shared/components/virtual-tour/services/virtual-tour.service';
import { ModalTourComponent } from '@serasa/shared/components/virtual-tour/sub-components/modal-tour/modal-tour.component';
import { Revision } from '@serasa/shared/interfaces/revision.interface';
import { Utils } from '@serasa/shared/utils/utils';
import {
  forkJoin,
  from,
  Observable,
  skip,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';

import * as NotificationsActions from '../notifications/state/notifications.actions';
import * as UserActions from '../users/state/actions/user.actions';

import { SedsModalService } from 'experian-design-system';
import { Labels } from '../../../shared/labels';
import {
  Notifications,
  RecertificationType,
  StatusType,
} from '../notifications/interfaces/notification-state.interface';
import { IUserNotification } from '../notifications/interfaces/notifications.interface';
import { selectNotifications } from '../notifications/state/notifications.selectors';
import { Terms } from '../privacy/interfaces/privacy-state.interface';
import { TermsStatus } from '../privacy/interfaces/terms-status.enum';
import { selectTerms } from '../privacy/state/selectors/privacy.selector';
import {
  RecertificationModel,
  RecertificationStatusEnum,
} from '../recertification/interfaces/recertification-model.interface';
import { UserMe } from '../recertification/interfaces/user-me.interface';
import { RecertificationService } from '../recertification/services/recertification.service';
import { selectUserMeInfo } from '../users/state/selectors/user.selectors';

@Component({
    selector: 'pl-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  public isOnTour!: boolean;
  public notifications: Notifications[] = [];
  public newFeaturesDescription = '';
  public labels = new Labels();
  public currentNotification?: IUserNotification;

  private privacySelector$!: Observable<Terms[]>;
  private userMeInfo$!: Observable<UserMe | null | undefined>;

  private _currentTerm?: Terms;
  private readonly _subs: Subscription[] = [];
  private _isRecertificationMandatory = false;
  private notifications$ = this.store.select(selectNotifications);
  private _hasCheckedVirtualTour = false;

  constructor(
    private readonly _sedsModalService: SedsModalService,
    private readonly _dialog: MatDialog,
    private readonly _recertificationService: RecertificationService,
    private readonly _router: Router,
    private readonly _virtualTourService: VirtualTourService,
    private readonly store: Store,
  ) { }

  ngOnInit(): void {
    const dispatch1 = from([this.store.dispatch(UserActions.loadUserMeInfo())]);
    const dispatch2 = from([this.store.dispatch(NotificationsActions.loadNotifications())]);

    forkJoin({ dispatch1, dispatch2 });

    this.privacySelector$ = this.store.select(selectTerms);
    this.userMeInfo$ = this.store.select(selectUserMeInfo);

    localStorage.removeItem('searchValue');

    this._verifyIfUserRecertificationIsMandatory();
    this._verifyUserNotifications();

  }

  public checkRecertification(recertification: RecertificationModel): void {
    localStorage.removeItem('recertificationData');

    if (recertification !== null && recertification?.status !== 'ATTESTED') {
      const notif: Notifications = {
        display: 'block',
        isClosable: recertification?.status !== 'EXPIRED',
        recertification: {
          remainingDays: `${recertification?.remainingDays}`,
          limitDate: recertification?.limitDate,
          status: recertification?.status as RecertificationType,
        },
        status: 'warning' as StatusType,
        type: 'recertification',
      };

      this._isRecertificationMandatory =
        recertification?.status === RecertificationStatusEnum.MANDATORY;
      this._createRecertificationData(recertification);

      this.notifications.push(notif);
    } else if (recertification === null) {
      this.notifications = [];
    }
  }

  public checkVirtualTour(virtualTour: VirtualTour): void {
    if (virtualTour.enabled && !this._isRecertificationMandatory) {
      this._dialog.closeAll();

      const isDashboardPage = this._includesDashboard();
      if (isDashboardPage) {
        this._sedsModalService.open(ModalTourComponent, null, {
          width: '600px',
          height: '302px',
        });
      }
    }

    this._virtualTourService.isOnTour.subscribe((res) => {
      this.isOnTour = res;
    });
  }

  public checkAccessesRevision(revisionData: Revision): void {
    if (revisionData.logonsTotal > 0) {
      const revision: Notifications = {
        display: 'inline',
        isClosable: false,
        header: 'Atenção',
        status: 'warning' as StatusType,
        type: 'revision',
        revision: {
          logonsTotal: revisionData.logonsTotal,
        },
      };
      this.notifications.push(revision);
    }
  }

  checkIsDistributor(channel: {
    channelCode: number;
    channelDescription: string;
  }) {
    if (channel?.channelCode === 3) {
      const distributor: Notifications = {
        display: 'inline',
        isClosable: false,
        header: 'Atenção',
        status: 'info' as StatusType,
        type: 'distribution',
      };
      this.notifications.push(distributor);
    }
  }

  private _checkUserInfo(userMeInfo: UserMe): void {
    this.notifications = [];

    this._recertificationService.manager.next(userMeInfo);
    this.checkRecertification(userMeInfo?.recertification);
    this._verifyTerms();
    this.checkAccessesRevision(userMeInfo?.revision);
    this.checkIsDistributor(userMeInfo?.channel);

    if (!this._hasCheckedVirtualTour) {
      this._hasCheckedVirtualTour = true;
      setTimeout(() => {
        this.checkVirtualTour(userMeInfo?.virtualTour);
      }, 1000);
    }
  }

  private _verifyTerms(): void {
    this._subs.push(
      this.privacySelector$
        .pipe(
          tap((terms) => {
            if (terms?.length) {
              this._currentTerm = terms[0];
              if (this._currentTerm.status === TermsStatus.PENDING) {
                this._dialog.closeAll();

                setTimeout(() => {
                  this._router.navigate(['privacy-terms']);
                }, 1000);
                return;
              }
            }
          }),
          takeUntil(Utils.componentDestroyed(this))
        )
        .subscribe()
    );
  }

  private _verifyIfUserRecertificationIsMandatory(): void {
    this.userMeInfo$.pipe(skip(1)).subscribe((userMeInfo) => {
      if (userMeInfo) {
        if (
          userMeInfo?.recertification?.status ===
          RecertificationStatusEnum.MANDATORY
        ) {
          this._router.navigate(['recertification']);
        } else {
          this._checkUserInfo(userMeInfo);
        }
      }
    });
  }

  private _createRecertificationData(info: RecertificationModel): void {
    const { remainingDays, limitDate, status } = info;

    const recertification = {
      remainingDays: remainingDays?.toString()?.padStart(2, '0'),
      limitDate: limitDate?.slice(0, 10),
      status: status,
    };

    localStorage.setItem(
      'recertificationData',
      JSON.stringify(recertification)
    );
  }

  private _includesDashboard(): boolean {
    return location.href.includes('dashboard');
  }

  private _verifyUserNotifications(): void {
    this._subs.push(
      this.notifications$.subscribe((notification) => {
        if (notification.userNotifications?.length) {
          this.currentNotification = notification.userNotifications[0];
        }
      })
    );
  }

  ngOnDestroy() {
    for (const sub of this._subs) {
      sub.unsubscribe();
    }
  }
}
